import { ResettingFileUpload } from '@/components/FileUpload/ResettingFileUpload';
import { isErrorWithData, isErrorWithErrors, saveFile } from '@/services/helpers';
import { useImportProjectTransactionsCsvMutation } from '@/services/payouts';
import { Alert, Box, Button, Modal, toast, useOpenClose } from '@palmetto/palmetto-components';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';

interface UploadError {
  title: string;
  items?: string[];
}

// A failure with no 4xx body can arrive after some account groups were already written.
const unexpectedError: UploadError = {
  title: 'The upload did not finish.',
  items: ['Some transactions may have been created. Check the transactions list before uploading this file again.'],
};

const toUploadError = (error: unknown): UploadError => {
  if (isErrorWithErrors(error)) {
    return { title: error.data.message, items: error.data.errors.length > 1 ? error.data.errors : undefined };
  }
  if (isErrorWithData(error)) return { title: error.data.message };
  return unexpectedError;
};

export const ImportTransactionsCsv = () => {
  const { isOpen, handleOpen, handleClose } = useOpenClose();
  const [importCsv, { isLoading }] = useImportProjectTransactionsCsvMutation();
  const [files, setFiles] = useState<FileList | undefined>();
  const [uploadError, setUploadError] = useState<UploadError | undefined>();
  const file = files?.[0];

  const errorItems = useMemo(
    () =>
      uploadError?.items && (
        <Box as="ul" childGap="2xs" padding="0 0 0 lg">
          {uploadError.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </Box>
      ),
    [uploadError],
  );

  const reset = useCallback(() => {
    setFiles(undefined);
    setUploadError(undefined);
  }, []);

  const handleDismiss = useCallback(() => {
    // Closing would not abort the request, and its download would then fire from a closed modal.
    if (isLoading) return;
    reset();
    handleClose();
  }, [isLoading, reset, handleClose]);

  const handleFileChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setFiles(event.target.files ?? undefined);
    setUploadError(undefined);
  }, []);

  const handleUpload = useCallback(async () => {
    if (!file) return;
    setUploadError(undefined);
    try {
      const { csv, fileName } = await importCsv(file).unwrap();
      // response.text() drops the BOM, and Excel needs it to read the file as UTF-8.
      saveFile(new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8' }), fileName);
      toast.success('Import finished. Check the status column in the results file.');
      reset();
      handleClose();
    } catch (error) {
      setUploadError(toUploadError(error));
    }
  }, [file, importCsv, reset, handleClose]);

  return (
    <>
      <Button size="sm" variant="primary" onClick={handleOpen}>
        Bulk Create
      </Button>
      <Modal isOpen={isOpen} onDismiss={handleDismiss} maxWidth="4xl" ariaLabelledBy="uploadTransactionsCsvHeader">
        <Modal.Header
          id="uploadTransactionsCsvHeader"
          title="Bulk Create Transaction CSV"
          onDismiss={handleDismiss}
        />
        <Modal.Body background="secondary" childGap="lg">
          <Box>
            Upload a .csv of transactions. On success, an output file will be provided that contains what was
            created.
          </Box>
          <ResettingFileUpload
            id="transactionsCsv"
            name="transactionsCsv"
            labelText="Choose File"
            buttonText="Choose File"
            accept=".csv"
            variant="secondary"
            tone="neutral"
            size="md"
            onChange={handleFileChange}
            files={files}
            isDisabled={isLoading}
          />
          {uploadError && <Alert variant="danger" hasIcon title={uploadError.title} message={errorItems} />}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" tone="neutral" isDisabled={isLoading} onClick={handleDismiss}>
            Cancel
          </Button>
          <Button variant="primary" isLoading={isLoading} isDisabled={!file || isLoading} onClick={handleUpload}>
            Create Transactions
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
